import { Box, Button as MuiButton, Link, Stack, useTheme } from "@mui/material";
import {
    Button as FoodsButton,
    DottedBox,
    FoodsDeleteButton,
    FoodsIcon,
    FormBox,
    MRTGrid,
    Typography,
} from "@foodpilot/foods";
import { useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { createMRTColumnHelper } from "material-react-table";
import { useGetSomeIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetSomeIngredientReferencesUnified";
import { IngredientReferenceUnified, buildCompositeId } from "src/api/client-api/ingredientReferenceUnified/types";
import { SupplierIngredientsModal } from "./SupplierIngredientsModal/SupplierIngredientsModal";
import { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import { SupplierFormAppearance, SupplierFormIngredientSupplier, SupplierFormType } from "../../SupplierForm.types";
import {
    IngredientSupplierTransportControl,
    IngredientSupplierTransportDialog,
    IngredientSupplierTransportTableCell,
} from "src/components/Transport/IngredientSupplierTransportFields";
import type {
    IngredientSupplierTransportChangeReason,
    IngredientSupplierTransportSelection,
} from "src/components/Transport/ingredientSupplierTransportTypes";
import { SupplierService } from "src/pages/Supplier/SupplierService";
import {
    AddItemField,
    FormFieldLoadError,
    FormItemRow,
    SectionCard,
    SectionContent,
    SectionHeader,
} from "src/components/EntityForm";
import { GridText } from "src/components/Grid/GridText";
import ManageElemIcon from "src/assets/icons/manage-elements-black.svg?react";

type Page3Props = {
    appearance?: SupplierFormAppearance;
    supplier: SupplierFormType;
    setSupplier: Dispatch<SetStateAction<SupplierFormType>>;
    existingSupplier?: Company;
    progress?: { current: number; total: number };
    showTransport?: boolean;
};

const Page3 = (props: Page3Props) => {
    const { appearance = "legacy", supplier, setSupplier, progress, showTransport = true } = props;
    const { t } = useTranslation();
    const [isOpen, setOpen] = useState(false);
    const openModal = () => setOpen(true);
    const setIngredientTransportSelections = (
        ingredientIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) => {
        setSupplier((current) => ({
            ...current,
            ingredientSuppliers: SupplierService.updateTransportSelections(
                current.ingredientSuppliers,
                ingredientIri,
                transportSelections,
                reason,
            ),
        }));
    };

    const modal = (
        <SupplierIngredientsModal
            appearance={appearance}
            existingIngredientSuppliers={supplier.ingredientSuppliers}
            isOpen={isOpen}
            setOpen={setOpen}
            onValidate={(newIngredients) => {
                setSupplier((current) => ({
                    ...current,
                    ingredientSuppliers: newIngredients,
                }));
            }}
        />
    );

    if (appearance === "legacy") {
        return (
            <LegacyPage3
                modal={modal}
                ingredients={supplier.ingredientSuppliers}
                openModal={openModal}
                showTransport={showTransport}
                onChangeTransportSelections={setIngredientTransportSelections}
            />
        );
    }

    return (
        <SectionCard data-testid="supplier-ingredients-section">
            <SectionHeader
                progress={progress}
                title={t("menu.products.ingredients", "Ingredients and transport")}
                subtitle={t(
                    "supplier.form.ingredients.subtitle",
                    "Associate ingredients and define the transport circuits used by this supplier.",
                )}
            />
            <SectionContent>
                {modal}
                <DisplayIngredientsFromSuppliers
                    ingredients={supplier.ingredientSuppliers}
                    openModal={openModal}
                    showTransport={showTransport}
                    onRemove={(ingredientIri) =>
                        setSupplier((current) => ({
                            ...current,
                            ingredientSuppliers: current.ingredientSuppliers.filter(
                                (row) => row.ingredient["@id"] !== ingredientIri,
                            ),
                        }))
                    }
                    onChangeTransportSelections={setIngredientTransportSelections}
                />
            </SectionContent>
        </SectionCard>
    );
};

type LegacyPage3Props = {
    ingredients: SupplierFormIngredientSupplier[];
    modal: ReactNode;
    onChangeTransportSelections: DisplayIngredientsFromSuppliersProps["onChangeTransportSelections"];
    openModal: () => void;
    showTransport: boolean;
};

const LegacyPage3 = ({
    ingredients,
    modal,
    onChangeTransportSelections,
    openModal,
    showTransport,
}: LegacyPage3Props) => {
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(4)}>
            {modal}
            {ingredients.length > 0 ?
                <LegacyDisplayIngredientsFromSuppliers
                    ingredients={ingredients}
                    onChangeTransportSelections={onChangeTransportSelections}
                    openModal={openModal}
                    showTransport={showTransport}
                />
            :   <LegacyNoIngredient openModal={openModal} />}
        </Stack>
    );
};

const LegacyNoIngredient = ({ openModal }: { openModal: () => void }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <FormBox>
            <DottedBox>
                <Stack alignItems="center" gap={theme.spacing(2)} sx={{ padding: theme.spacing(4) }}>
                    <Typography variant="big-medium">{t("aucun_ingredient_associe")}</Typography>
                    <FoodsButton variant="text" startIcon={<FoodsIcon icon="plus" size={2} />} onClick={openModal}>
                        {t("associer_des_ingredients")}
                    </FoodsButton>
                </Stack>
            </DottedBox>
        </FormBox>
    );
};

const LegacyDisplayIngredientsFromSuppliers = ({
    ingredients,
    onChangeTransportSelections,
    openModal,
    showTransport,
}: Omit<DisplayIngredientsFromSuppliersProps, "onRemove">) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [transportRow, setTransportRow] = useState<SupplierFormIngredientSupplier | null>(null);
    const compositeIds = ingredients
        .map((row) =>
            row.ingredient.ingredientReferenceId === undefined ?
                undefined
            :   buildCompositeId(row.ingredient.referenceSource ?? "sure", row.ingredient.ingredientReferenceId),
        )
        .filter((id): id is string => id !== undefined);
    const { ingredientReferences, isError, isLoading, refetch } = useGetSomeIngredientReferencesUnified(compositeIds);
    const referencesByCompositeId = (ingredientReferences ?? []).reduce(
        (references, reference) => {
            references[reference.compositeId] = reference;
            return references;
        },
        {} as Record<string, IngredientReferenceUnified>,
    );
    const getTransportSelections = (row: SupplierFormIngredientSupplier): IngredientSupplierTransportSelection =>
        row.transportSelections ?? {};
    const columnHelper = createMRTColumnHelper<SupplierFormIngredientSupplier>();
    const columns = [
        columnHelper.accessor((row) => row.ingredient.name, {
            enableSorting: false,
            id: "name",
            header: t("nom"),
            Cell: ({ cell }) => <Link sx={{ color: "inherit", textDecoration: "none" }}>{cell.getValue()}</Link>,
        }),
        columnHelper.accessor(
            (row) => {
                const referenceId = row.ingredient.ingredientReferenceId;
                if (referenceId === undefined) return "-";
                if (isLoading) return t("common.loading", "Loading...");

                const compositeId = buildCompositeId(row.ingredient.referenceSource ?? "sure", referenceId);
                return referencesByCompositeId[compositeId]?.name ?? "-";
            },
            {
                id: "ingredient-ref",
                header: t("reference", "Reference"),
                Cell: ({ cell }) => <Typography variant="body">{cell.getValue()}</Typography>,
            },
        ),
        columnHelper.accessor((row) => row.ingredient.ingredientLine?.name ?? "-", {
            id: "ingredientLine",
            header: t("filiere"),
            Cell: ({ cell }) => <GridText value={cell.getValue()} />,
        }),
        ...(showTransport ?
            [
                columnHelper.display({
                    id: "transport",
                    header: t("transport", "Transport"),
                    enableSorting: false,
                    Cell: ({ row }) => (
                        <IngredientSupplierTransportTableCell
                            appearance="legacy"
                            ingredientSupplierId={extractIdFromIri(row.original["@id"])}
                            value={getTransportSelections(row.original)}
                            onConfigure={() => setTransportRow(row.original)}
                        />
                    ),
                }),
            ]
        :   []),
    ];

    return (
        <Box sx={{ display: "flex", gap: theme.spacing(4) }}>
            <FormBox sx={{ flex: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "row-reverse", marginBottom: theme.spacing(4) }}>
                    <MuiButton variant="ternary" startIcon={<ManageElemIcon />} onClick={openModal}>
                        <Typography variant="body-bold">{t("associer_ou_dissocier_des_ingredients")}</Typography>
                    </MuiButton>
                </Box>
                {isError && (
                    <FormFieldLoadError message={t("une_erreur_est_survenue", "An error occurred")} onRetry={refetch} />
                )}
                <MRTGrid
                    columns={columns}
                    data={ingredients}
                    enableSorting={false}
                    enableRowSelection={false}
                    state={{ isLoading }}
                />
                {showTransport && transportRow !== null && (
                    <IngredientSupplierTransportDialog
                        appearance="legacy"
                        open
                        saveMode="deferred"
                        onClose={() => setTransportRow(null)}
                        title={transportRow.ingredient.name}
                        ingredientSupplierId={extractIdFromIri(transportRow["@id"])}
                        value={getTransportSelections(transportRow)}
                        onChange={(next, reason) =>
                            onChangeTransportSelections(transportRow.ingredient["@id"], next, reason)
                        }
                    />
                )}
            </FormBox>
        </Box>
    );
};

type DisplayIngredientsFromSuppliersProps = {
    ingredients: SupplierFormIngredientSupplier[];
    openModal: () => void;
    showTransport: boolean;
    onRemove: (ingredientIri: IRI) => void;
    onChangeTransportSelections: (
        ingredientIri: IRI,
        transportSelections: IngredientSupplierTransportSelection,
        reason: IngredientSupplierTransportChangeReason,
    ) => void;
};
const DisplayIngredientsFromSuppliers = (props: DisplayIngredientsFromSuppliersProps) => {
    const { ingredients, openModal, showTransport, onRemove, onChangeTransportSelections } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [transportRow, setTransportRow] = useState<SupplierFormIngredientSupplier | null>(null);
    const getTransportSelections = (row: SupplierFormIngredientSupplier): IngredientSupplierTransportSelection => {
        return row.transportSelections ?? {};
    };

    // Batch-fetch only references rendered in the form rows to keep large suppliers cheap.
    const compositeIds = ingredients
        .map((ing) =>
            ing.ingredient.ingredientReferenceId !== undefined ?
                buildCompositeId(ing.ingredient.referenceSource ?? "sure", ing.ingredient.ingredientReferenceId)
            :   undefined,
        )
        .filter((id): id is string => id !== undefined);
    const { ingredientReferences, isError, isLoading, refetch } = useGetSomeIngredientReferencesUnified(compositeIds);
    const compositeIdToRef = (ingredientReferences ?? []).reduce(
        (acc, ref) => {
            acc[ref.compositeId] = ref;
            return acc;
        },
        {} as Record<string, IngredientReferenceUnified>,
    );

    const getReferenceName = (row: SupplierFormIngredientSupplier) => {
        const referenceId = row.ingredient.ingredientReferenceId;
        if (referenceId === undefined) return "-";
        if (isLoading) return t("common.loading", "Loading...");

        const compositeId = buildCompositeId(row.ingredient.referenceSource ?? "sure", referenceId);
        return compositeIdToRef[compositeId]?.name ?? "-";
    };

    return (
        <Stack gap={theme.spacing(3)}>
            {isError && (
                <FormFieldLoadError message={t("une_erreur_est_survenue", "An error occurred")} onRetry={refetch} />
            )}

            {ingredients.map((row) => (
                <FormItemRow
                    density="comfortable"
                    key={row["@id"] ?? row.ingredient["@id"]}
                    testId="supplier-ingredient-row"
                >
                    <FieldSummary label={t("nom", "Name")} value={row.ingredient.name} />
                    <FieldSummary label={t("reference", "Reference")} value={getReferenceName(row)} />
                    <FieldSummary label={t("filiere", "Industry")} value={row.ingredient.ingredientLine?.name ?? "-"} />

                    {showTransport && (
                        <Stack
                            data-testid="supplier-ingredient-transport"
                            gap={theme.spacing(1)}
                            sx={{ flex: `2 1 ${theme.spacing(40)}`, minWidth: 0 }}
                        >
                            <Typography variant="small-semibold" sx={{ color: theme.custom.grey[2000] }}>
                                {t("transport", "Transport")}
                            </Typography>
                            <IngredientSupplierTransportControl
                                appearance="product-v2"
                                ingredientSupplierId={extractIdFromIri(row["@id"])}
                                value={getTransportSelections(row)}
                                onConfigure={() => setTransportRow(row)}
                            />
                        </Stack>
                    )}
                    <FoodsDeleteButton
                        ariaLabel={`${t("common.actions.delete", "Delete")} ${row.ingredient.name}`}
                        onClick={() => onRemove(row.ingredient["@id"])}
                        buttonProps={{ type: "button" }}
                        tooltip={{ text: t("common.actions.delete", "Delete") }}
                    />
                </FormItemRow>
            ))}

            <AddItemField
                actionLabel={t("associer_ou_dissocier_des_ingredients", "Associate or dissociate ingredients")}
                description={
                    ingredients.length === 0 ?
                        t(
                            "supplier.form.ingredients.emptyDescription",
                            "Associate the ingredients supplied by this organization.",
                        )
                    :   undefined
                }
                onAction={openModal}
            />

            {showTransport && transportRow !== null && (
                <IngredientSupplierTransportDialog
                    appearance="product-v2"
                    open
                    saveMode="deferred"
                    onClose={() => setTransportRow(null)}
                    title={transportRow.ingredient.name}
                    ingredientSupplierId={extractIdFromIri(transportRow["@id"])}
                    value={getTransportSelections(transportRow)}
                    onChange={(next, reason) =>
                        onChangeTransportSelections(transportRow.ingredient["@id"], next, reason)
                    }
                />
            )}
        </Stack>
    );
};

const FieldSummary = ({ label, value }: { label: string; value: string }) => {
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(0.5)} sx={{ flex: `1 1 ${theme.spacing(22.5)}`, minWidth: 0 }}>
            <Typography variant="small-semibold" sx={{ color: theme.custom.grey[2000] }}>
                {label}
            </Typography>
            <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                {value}
            </Typography>
        </Stack>
    );
};

const extractIdFromIri = (iri?: IRI): number | undefined => {
    if (iri === undefined) {
        return undefined;
    }

    const rawId = iri.split("/").pop();
    const id = rawId === undefined ? NaN : Number.parseInt(rawId, 10);

    return Number.isNaN(id) ? undefined : id;
};
export default Page3;
